import React, { FC, useEffect, useState, useCallback } from "react";
import { useAuth } from "../auth/authContext";
import { useResumeManager } from "../services/useResumeManager";
import { Resume } from "../types/resume";
import { ResumeVersion } from "../types/resumeVersion";
import { userService } from "../api/userService";

const ResumeSkills: FC = () => {
    const { user } = useAuth();
    const { resumes, busy, error, create, refetch } = useResumeManager(user?.id);
    const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
    const [selectedVersion, setSelectedVersion] = useState<ResumeVersion | null>(null);
    const [resumeName, setResumeName] = useState("");
    const [resumeText, setResumeText] = useState("");
    const [originalResumeText, setOriginalResumeText] = useState("");
    const [isArchived, setIsArchived] = useState(false);
    const [isVersion, setIsVersion] = useState(false);
    const [isNewResume, setIsNewResume] = useState(true);
    const [availableVersions, setAvailableVersions] = useState<ResumeVersion[]>([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [, setOriginalIsArchived] = useState(false);

    useEffect(() => {
        if (originalResumeText) {
            setHasUnsavedChanges(resumeText !== originalResumeText);
        }
    }, [resumeText, originalResumeText]);

    useEffect(() => {
        if (selectedResume && resumes.length > 0) {
            const refreshedResume = resumes.find(r => r.resumeId === selectedResume.resumeId);
            if (refreshedResume) {
                setAvailableVersions(refreshedResume.resumeVersions);
                setSelectedResume(refreshedResume);
                setIsArchived(refreshedResume.isArchived);
                setOriginalIsArchived(refreshedResume.isArchived);
                setResumeName(refreshedResume.resumeTitle);
            }
        }
    }, [resumes, selectedResume]);

    const handleResumeSelect = useCallback((resume: Resume) => {
        setAvailableVersions(resume.resumeVersions);
        setSelectedResume(resume);
        setResumeName(resume.resumeTitle);
        setIsArchived(resume.isArchived);
        setOriginalIsArchived(resume.isArchived);
        setIsNewResume(false);

        setHasUnsavedChanges(false);
        setMessage(null);
    }, []);

    const handleVersionSelect = (versionId: string) => {
        const version = availableVersions.find(v => v.resumeVersionId === versionId);
        if (version) {
            setSelectedVersion(version);
            setResumeText(version.resumeText);
            setOriginalResumeText(version.resumeText);
            setHasUnsavedChanges(false);
            setMessage(null);
            setIsVersion(!version.isDraft);
        }
    };

    const handleIsArchived = (is_archived: boolean) => {
        setIsArchived(is_archived);
        console.log("is_archived:", is_archived);
    };

    const handleNewResume = useCallback(() => {
        setSelectedResume(null);
        setSelectedVersion(null);
        setResumeName("");
        setResumeText("");
        setOriginalResumeText("");
        setAvailableVersions([]);
        setIsArchived(false);
        setOriginalIsArchived(false);
        setIsNewResume(true);
        setHasUnsavedChanges(false);
        setMessage(null);
    }, []);

    const handleSaveDraft = async () => {
        if (!selectedResume || !user?.id) return;

        try {
            const { saveDraft } = userService();
            await saveDraft(user.id, selectedVersion?.resumeVersionId || "", resumeText);

            setOriginalResumeText(resumeText);
            setHasUnsavedChanges(false);

            await refetch();

            setMessage("✓ Draft version saved successfully.");
        } catch (err: any) {
            setMessage("ERROR: Failed to save draft version");
        }
    };

    const handleSaveToVersion = async () => {
        if (!selectedVersion || !user?.id) return;

        try {
            const { saveDraftToVersion } = userService();
            await saveDraftToVersion(user.id, selectedVersion.resumeVersionId);

            setOriginalResumeText(resumeText);
            setHasUnsavedChanges(false);

            await refetch();

            setAvailableVersions(selectedResume?.resumeVersions || []);
            setMessage("✓ Draft promoted to published version");
        } catch (err: any) {
            setMessage("ERROR: Failed to save draft to version");
        }
    };

    const handleSaveToResume = async () => {
        if (!selectedResume || !user?.id) return;

        try {
            const { saveToResume } = userService();
            await saveToResume(user.id, resumeName, selectedVersion?.resumeVersionId || "");

            await refetch();

            setMessage("✓ Draft promoted to published version");
        } catch (err: any) {
            setMessage("ERROR: Failed to promote draft to version");
        }
    };

    const handleSave = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (isNewResume) {
            const created = await create(resumeName, resumeText, isArchived, "User", false);
            if (created) {
                setSelectedVersion(created);
                setIsNewResume(false);
                await refetch();
            }
        } 
    }, [isNewResume, resumeName, resumeText, isArchived, create, refetch]);

    const isDraft = selectedVersion?.isDraft || false;
    
    return (
        <div className='settings-container'>
            <div style={{ display: 'flex', gap: '2rem', padding: '1.5rem' }}>
                {/* Resume List */}
                <div style={{ width: '400px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <label className='labelText'>Your Resumes:</label>
                        <button type="button" className='btn' onClick={handleNewResume}>
                            + New
                        </button>
                    </div>
                    <div style={{ border: '1px solid #ccc', height: '300px', overflowY: 'auto', borderRadius: '0.5rem' }}>
                        {resumes.map((resume) => (
                            <div
                                key={resume.resumeId}
                                onClick={() => handleResumeSelect(resume)}
                                style={{
                                    padding: '0.75rem',
                                    cursor: 'pointer',
                                    backgroundColor: selectedResume?.resumeId === resume.resumeId ? '#38bdf8' : 'transparent',
                                    borderBottom: '1px solid #eee',
                                    color: selectedResume?.resumeId === resume.resumeId ? 'black' : 'white',
                                    transition: 'background-color 0.2s'
                                }}
                            >
                                <div style={{ fontWeight: 500 }}>{resume.resumeTitle}</div>
                                <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.25rem' }}>
                                    v{resume.resumeVersions[0]?.version || 1} • {resume.resumeVersions.length} version{resume.resumeVersions.length !== 1 ? 's' : ''}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                        <label className='labelText'>Version:</label>
                        <select
                            className='resume-select'
                            style={{ width: '100%', padding: '0.6rem', borderRadius: '0.375rem', fontSize: '0.9rem', cursor: 'pointer' }}
                            value={selectedVersion?.resumeVersionId || ""}
                            onChange={(e) => handleVersionSelect(e.target.value)}
                            disabled={!selectedResume}
                        >
                            <option value="">Select version...</option>
                            {availableVersions.map(version => (
                                <option key={version.resumeVersionId} value={version.resumeVersionId}>
                                    v{version.version} - {new Date(version.createdUtc).toLocaleDateString()} {version.isDraft ? " : (Draft)" : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                
                {/* Resume Form */}
                <div style={{ flex: 1 }}>
                    <form onSubmit={handleSave}>
                        <div style={{ marginBottom: '1rem' }}>
                            <label className='labelText'>
                                Resume Name:
                                {hasUnsavedChanges && (
                                    <span style={{ 
                                        marginLeft: '0.5rem', 
                                        fontSize: '0.75rem', 
                                        color: '#fbbf24',
                                        background: 'rgba(251, 191, 36, 0.1)',
                                        padding: '0.15rem 0.5rem',
                                        borderRadius: '0.25rem',
                                        border: '1px solid rgba(251, 191, 36, 0.3)'
                                    }}>
                                        Unsaved Changes
                                    </span>
                                )}
                            </label>
                            {isNewResume || isVersion ? (
                            <input
                                type='text'
                                className='skillsInput'
                                style={{ width: '100%' }}
                                value={resumeName}
                                onChange={(e) => setResumeName(e.target.value)}
                                placeholder='e.g., Senior Developer Resume'
                                required
                            />
                            ) : (
                              <label>{resumeName}</label>  
                            )}
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label className='labelText'>Resume Text:</label>
                            <textarea
                                className='resume-textarea'
                                value={resumeText}
                                onChange={(e) => setResumeText(e.target.value)}
                                placeholder='Paste your resume text here'
                                rows={15}
                                cols={95}
                                required
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', minHeight: '28px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type='checkbox'
                                    checked={isArchived}
                                    onChange={(e) => handleIsArchived(e.target.checked)}
                                />
                                <span>Archive</span>
                            </label>
                            {/* {hasMetadataChanges && (
                                <button
                                    type="button"
                                    className='btn'
                                    onClick={handleUpdateMetadata}
                                    disabled={busy}
                                    style={{
                                        width: '150px',
                                        height: '25px',
                                        padding: '0.2rem 0.5rem',
                                        fontSize: '0.75rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    Update Resume
                                </button>
                            )} */}
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <button
                                type="submit"
                                className='btn'
                                disabled={busy}
                                style={{
                                    width: '170px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: !isNewResume ? 0.5 : 1
                                }}
                            >
                                {busy ? "Creating..." : "Save"}
                            </button>
                            <button
                                type="button"
                                className='btn btn-primaary'
                                onClick={handleSaveDraft}
                                disabled={busy || !hasUnsavedChanges}
                                style={{
                                    width: '170px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: !hasUnsavedChanges ? 0.5 : 1
                                }}
                            >
                                Save Changes
                            </button>
                            <button
                                type="button"
                                className='btn'
                                onClick={handleSaveToVersion}
                                disabled={busy || !isDraft}
                                style={{
                                    width: '170px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: !isDraft ? 0.5 : 1
                                }}
                            >
                                Save as New Version
                            </button>
                            <button
                                type="button"
                                className='btn'
                                onClick={handleSaveToResume}
                                onMouseEnter={() => setMessage("Change resume name before saving")}
                                onMouseLeave={() => setMessage(null)}
                                disabled={busy || !isVersion}
                                style={{
                                    width: '170px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    opacity: !isVersion ? 0.5 : 1
                                }}
                            >
                                Save as New Resume
                            </button>
                        </div>
                    </form>
                    {message && (
                        <div 
                            style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                //borderRadius: '0.375rem',
                                //backgroundColor: message.startsWith('✓') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: message.startsWith('✓') ? '#22c55e' : 'white',
                                //border: `1px solid ${message.startsWith('✓') ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)'}`,
                            }}
                        >{message}</div>
                    )}
                    {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
                </div>
            </div>
        </div>
    );
}

export default ResumeSkills;